// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Route, ClassId } from '../types';
import { esc, num, pct, pctv, money, compact, fmtValue, countryColour, shortName } from '../format';
import { squarify } from '../utils/squarify';
import { navigate } from '../main';
import { classCountries } from '../analysis';

export function renderAssets(root: HTMLElement, d: Dataset, route: Route): void {
  const order = d.meta.classOrder;
  const store = 'au-foreign-owned:assets-cls';
  let cid: ClassId = route.cls || (localStorage.getItem(store) as ClassId) || 'agricultural';
  if (!order.includes(cid)) cid = 'agricultural';

  function draw(): void {
    localStorage.setItem(store, cid);
    const meta = d.meta.classMeta[cid];
    const cls = d.classes[cid];
    const list = classCountries(d, cid).filter((c) => (c.value ?? 0) > 0);
    const total = list.reduce((a, c) => a + (c.value ?? 0), 0);
    const hhi = list.slice(0, 3).reduce((a, c) => a + (c.value ?? 0), 0) / (total || 1);

    root.querySelector('.assets-body')!.innerHTML = `
      <div class="stat-row">
        <div class="stat"><div class="stat-label">Source countries</div><div class="stat-value">${num(list.length)}</div></div>
        <div class="stat"><div class="stat-label">Total foreign-held</div><div class="stat-value">${cid === 'commercial' ? money(cls.totalDollars) : fmtValue(cid, total)}</div><div class="stat-note">${meta.unitLabel}</div></div>
        <div class="stat"><div class="stat-label">Top country</div><div class="stat-value" style="font-size:var(--font-size-lg)">${esc(shortName(list[0]?.name ?? '—'))}</div><div class="stat-note">${pctv((list[0]?.share ?? 0) * 100, 0)} of foreign-held</div></div>
        <div class="stat hl"><div class="stat-label">Top-3 concentration</div><div class="stat-value">${pctv(hhi * 100, 0)}</div><div class="stat-note">held by the three largest sources</div></div>
      </div>

      <div class="panel">
        <div class="panel-head"><h3>${esc(meta.label)} — country composition</h3>
        <p>Each rectangle is a source country, sized by ${cid === 'commercial' ? 'value ($)' : meta.unitLabel}. Click one for its full profile. Hover for exact figures.</p></div>
        <div class="chart-wrap" id="tm"></div>
      </div>

      ${detailPanel(d, cid)}
    `;
    drawTreemap(root.querySelector('#tm') as HTMLElement, cid, list);
    root.querySelectorAll<HTMLElement>('#tm [data-country]').forEach((el) => el.addEventListener('click', () => navigate({ view: 'assets', country: el.dataset.country })));
  }

  root.innerHTML = `
    <div class="view-head">
      <h2>By asset class</h2>
      <p>Each class in depth — how concentrated foreign ownership is among a handful of countries, and what kinds of land, industry or property it covers.</p>
    </div>
    <div class="controls">
      <span class="control-label">Asset class</span>
      <div class="seg">${order.map((c) => `<button data-cls="${c}" aria-pressed="${c === cid}">${esc(d.meta.classMeta[c].short)}</button>`).join('')}</div>
    </div>
    <div class="assets-body"></div>
  `;
  root.querySelectorAll<HTMLElement>('[data-cls]').forEach((b) => b.addEventListener('click', () => {
    cid = b.dataset.cls as ClassId;
    root.querySelectorAll<HTMLElement>('[data-cls]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    draw();
  }));
  draw();
}

function drawTreemap(el: HTMLElement, cid: ClassId, list: { name: string; iso: string; value: number | null; share: number; dollars?: number | null }[]): void {
  const W = 1000, H = 500;
  const vals = list.map((c) => (cid === 'commercial' ? (c.dollars ?? 0) : (c.value ?? 0)));
  const rects = squarify(vals, W, H);
  const cells = rects.map((r, i) => {
    const c = list[i];
    const col = countryColour(c.iso, c.name);
    const big = r.w > 74 && r.h > 30;
    const med = r.w > 40 && r.h > 18;
    const valStr = cid === 'commercial' ? money(c.dollars) : fmtValue(cid, c.value);
    const tip = `${c.name} — ${valStr}\n${pct(c.share)} of foreign-held`;
    const label = big
      ? `<text x="${r.x + 5}" y="${r.y + 15}" font-size="12" fill="#fff" font-weight="600" style="text-shadow:0 1px 2px rgba(0,0,0,.4)">${esc(shortName(c.name))}</text><text x="${r.x + 5}" y="${r.y + 30}" font-size="10" fill="#fff" opacity="0.92">${esc(valStr)}</text>`
      : med ? `<text x="${r.x + 4}" y="${r.y + 13}" font-size="10" fill="#fff" style="text-shadow:0 1px 2px rgba(0,0,0,.4)">${esc(shortName(c.name))}</text>` : '';
    return `<g class="node" data-country="${esc(c.name)}" data-tip="${esc(tip)}">
      <rect x="${r.x.toFixed(1)}" y="${r.y.toFixed(1)}" width="${Math.max(0, r.w - 1).toFixed(1)}" height="${Math.max(0, r.h - 1).toFixed(1)}" fill="${col}" stroke="#fff" stroke-width="1"/>${label}</g>`;
  }).join('');
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" role="img" aria-label="Country composition treemap" style="height:auto;aspect-ratio:${W}/${H}">${cells}</svg>`;
}

function bars(rows: { name: string; value: number | null }[], fmt: (v: number | null) => string, colour = 'var(--accent-primary)'): string {
  const max = Math.max(...rows.map((r) => r.value ?? 0)) || 1;
  return `<div class="barlist">${rows.map((r) => `
    <div class="barrow" style="cursor:default" data-tip="${esc(r.name)}: ${esc(fmt(r.value))}">
      <span class="lbl"><span style="min-width:0;overflow:hidden;text-overflow:ellipsis">${esc(r.name)}</span></span>
      <span class="track"><span style="width:${Math.max(1.5, ((r.value ?? 0) / max) * 100)}%;background:${colour}"></span></span>
      <span class="val">${esc(fmt(r.value))}</span>
    </div>`).join('')}</div>`;
}

function detailPanel(d: Dataset, cid: ClassId): string {
  const cls = d.classes[cid];
  if (cid === 'agricultural') {
    const lu = (cls.landUse ?? []).slice(-1)[0];
    if (!lu) return '';
    const f = (lu.foreign ?? 0), a = (lu.aust ?? 0), t = lu.total ?? 1;
    return `<div class="panel"><div class="panel-head"><h3>Foreign vs Australian share of registered parcels</h3>
      <p>Land the Register records is counted where a foreign person holds a substantial stake — but the same parcels are often largely Australian-owned.</p></div>
      <div style="display:flex;height:34px;border-radius:6px;overflow:hidden;border:1px solid var(--border-subtle)">
        <div data-tip="Foreign share: ${compact(f)} ha (${pctv((f / t) * 100, 0)})" style="width:${(f / t) * 100}%;background:var(--accent-warm);display:flex;align-items:center;justify-content:center;color:#fff;font-size:var(--font-size-sm)">Foreign ${pctv((f / t) * 100, 0)}</div>
        <div data-tip="Australian share: ${compact(a)} ha (${pctv((a / t) * 100, 0)})" style="width:${(a / t) * 100}%;background:var(--accent-primary);display:flex;align-items:center;justify-content:center;color:#fff;font-size:var(--font-size-sm)">Australian ${pctv((a / t) * 100, 0)}</div>
      </div>
      <p class="mini-note">Within ${compact(t)} ha of registered parcels at 30 June ${d.meta.latestYear}.</p></div>`;
  }
  if (cid === 'water') {
    return `<div class="panel"><div class="panel-head"><h3>About water entitlements</h3></div>
      <p class="mini-note">Water is registered separately from land and measured in gigalitres of entitlement. ${pctv(d.meta.counts.waterForeignSharePct)} of Australian water entitlements on issue carry a foreign interest — with Canada and the United States, not China, the largest holders.</p></div>`;
  }
  if (cid === 'residential') {
    const flow = cls.flow;
    if (!flow) return '';
    const ly = Math.max(...flow.years);
    const rows = Object.keys(flow.byType).map((t) => ({ name: t, value: Object.values(flow.byType[t]).reduce((s, byYear) => s + (byYear[ly] ?? 0), 0) })).sort((a, b) => b.value - a.value);
    return `<div class="panel"><div class="panel-head"><h3>What foreign buyers acquire · ${ly - 1}–${String(ly).slice(2)}</h3>
      <p>Foreign non-residents are generally restricted to new dwellings and vacant land, not established homes — which shapes the mix.</p></div>
      ${bars(rows, (v) => num(v) + ' acquisitions')}</div>`;
  }
  // commercial / business / mining industry
  const ind = (cls.industry ?? []).filter((r) => (r.latest ?? 0) > 0).slice(0, 12).map((r) => ({ name: r.name, value: r.latest }));
  if (!ind.length) return '';
  const title = cid === 'mining' ? 'By mineral / activity' : 'By industry';
  return `<div class="panel"><div class="panel-head"><h3>${title}</h3>
    <p>Registered foreign interests by ${cid === 'mining' ? 'mining activity' : 'ANZSIC industry division'} at 30 June ${d.meta.latestYear}.</p></div>
    ${bars(ind, (v) => num(v))}</div>`;
}
